import React from 'react';

import './metricCardsSkeletonLoader.scss';

interface MetricCardsSkeletonLoaderProps {
    /** Number of metric-card placeholders to render - matches a typical scorecard row. */
    cardCount?: number;
}

const GAUGE_SIZE = 170;

// A static dome outline (same geometry family as TieredMetricGauge/LegacyMetricGauge) so the
// skeleton reads as "a gauge is about to appear here" rather than a generic gray blob. Pulses via
// CSS rather than a literal shimmer sweep, since a curved stroke doesn't shimmer convincingly with
// a linear gradient the way rectangular text-line bars do.
const SkeletonGaugeDome = () => {
    const cx = GAUGE_SIZE / 2;
    const cy = GAUGE_SIZE / 2 + 16;
    const r = GAUGE_SIZE / 2 - 19;
    const d = `M ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${cx + r} ${cy}`;
    return (
        <svg width={GAUGE_SIZE} height={GAUGE_SIZE} viewBox={`0 0 ${GAUGE_SIZE} ${GAUGE_SIZE}`} className="skeleton-gauge-dome">
            <path d={d} fill="none" stroke="#E3E7EB" strokeWidth={30} strokeLinecap="butt" />
        </svg>
    );
};

const SkeletonCard = ({ delay }: { delay: number }) => (
    <div className="skeleton-metric-card" style={{ animationDelay: `${delay}ms` }}>
        <div className="skeleton-line skeleton-line--title skeleton-shimmer" />
        <div className="skeleton-line skeleton-line--pill skeleton-shimmer" />
        <SkeletonGaugeDome />
        <div className="skeleton-line skeleton-line--status skeleton-shimmer" />
        <div className="skeleton-divider" />
        <div className="skeleton-line skeleton-line--threshold skeleton-shimmer" />
        <div className="skeleton-line skeleton-line--threshold skeleton-shimmer" />
    </div>
);

const SkeletonTrendChart = () => (
    <div className="skeleton-trend-chart">
        <div className="skeleton-line skeleton-line--chart-title skeleton-shimmer" />
        <div className="skeleton-trend-chart__bars">
            {[62, 88, 45, 70, 55, 95, 40, 80, 60, 75, 50, 85].map((height, i) => (
                // eslint-disable-next-line react/no-array-index-key -- static placeholder list, order never changes
                <div key={i} className="skeleton-trend-chart__bar skeleton-shimmer" style={{ height: `${height}%` }} />
            ))}
        </div>
    </div>
);

/**
 * Rich loading state for the metric cards + trend chart area, shown while
 * useGetMonthlyMetricsSummaryQuery / useGetDailyMetricsSummaryQuery are in flight. Distinguishes
 * "still loading" from "genuinely no data" - previously both rendered the same "No results found"
 * message, which was misleading during a normal fetch.
 */
export const MetricCardsSkeletonLoader = ({ cardCount = 5 }: MetricCardsSkeletonLoaderProps) => (
    <div className="metric-cards-skeleton-loader" aria-busy="true" aria-label="Loading metrics">
        <div className="metric-cards-skeleton-loader__cards">
            {Array.from({ length: cardCount }, (_, i) => (
                // eslint-disable-next-line react/no-array-index-key -- static placeholder list, order never changes
                <SkeletonCard key={i} delay={i * 80} />
            ))}
        </div>
        <SkeletonTrendChart />
    </div>
);

export default MetricCardsSkeletonLoader;
